import { pushDialog } from '@src/components/Dialog/Dialog';
import { useParticipantIdentityEditor } from './ParticipantIdentityEditor';
import { OrganizerQuestionnaire } from './full/OrganizerQuestionnaire';
import { useNavigate } from 'react-router-dom';
import { boxCardEditPath } from '@src/routing/routes';
import { AtIcon } from '@src/components/icons/AtIcon';
import { VkIcon } from '@src/components/icons/VkIcon';
import { GoogleIcon } from '@src/components/icons/GoogleIcon';
import { TelegramIcon } from '@src/components/icons/TelegramIcon';
import { UserIcon } from '@src/components/icons/UserIcon';
import { ClockIcon } from '@src/components/icons/ClockIcon';
import { GiftIcon } from '@src/components/icons/GiftIcon';
import { PencilIcon } from '@src/components/icons/PencilIcon';
import { ParticipantGiftIcon, participantGifts } from './full/ParticipantGiftIndicators';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type Box, type CardInfo, type MaskedField } from '@lub0v/santa-utils';
import {
  Button,
  Cluster,
  Divider,
  ParticipantFace,
  Stack,
  Typography,
  showDialog,
  closeDialog,
} from '@src/components';
import { santaClient } from '@src/state/client';
import { participantStatus } from './participantStatus';
import { WishText } from '@src/components/WishText/WishText';
import './ParticipantPreview.css';

interface PreviewProps {
  card: CardInfo;
  people: CardInfo[];
  box: Pick<Box, 'key' | 'useWish' | 'usePost' | 'isPhoneRequired' | 'isArchived'>;
  isActiveDraw: boolean;
  index: number;
  copiedIds: Set<number>;
  onChanged?: () => Promise<void> | void;
  onEditIdentity?: () => void;
}

export function ParticipantPreview({
  card,
  people,
  box,
  isActiveDraw,
  index,
  copiedIds,
  onEditIdentity,
}: PreviewProps) {
  const { t, i18n } = useTranslation();
  const [wish, setWish] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const text = (key: string) => t(`box.organizer.preview.${key}`);
  const status = participantStatus(card, copiedIds);
  const gifts = participantGifts(card, people);
  const hasQuestionnaireLetter = card.customAnswers?.some(
    (answer) =>
      answer.field.type === 'santa_letter' &&
      answer.field.enabled !== false &&
      Boolean(answer.value?.trim()),
  );
  const shownTypes = new Set(card.customAnswers?.map((answer) => answer.field.type));
  const hiddenAnswers = (card.hiddenAnswers ?? []).filter(
    (answer) => !card.customAnswers?.some((visible) => visible.fieldId === answer.field.id),
  );
  const handledTypes = new Set([
    ...shownTypes,
    ...hiddenAnswers.map((answer) => answer.field.type),
  ]);
  const showLegacyLetter =
    box.useWish && !hasQuestionnaireLetter && !handledTypes.has('santa_letter');
  const showLegacyPhone = box.isPhoneRequired && !handledTypes.has('phone');
  const showLegacyAddress =
    box.usePost && !handledTypes.has('delivery_address') && !handledTypes.has('postal_address');
  const row = (label: string, value: ReactNode) => (
    <div className="participant-preview__row">
      <dt>{text(label)}</dt>
      <dd>{value}</dd>
    </div>
  );
  const filled = (field?: MaskedField) => (
    <Stack gap={1}>
      <span className="participant-preview__value">{text(field?.length ? 'filled' : 'empty')}</span>
      {field?.length ? (
        <span>
          {field.masked} · {t('box.organizer.preview.characters', { count: field.length })}
        </span>
      ) : null}
    </Stack>
  );
  const gift = (direction: 'outgoing' | 'incoming') => {
    const state = gifts[direction];
    return (
      <div className="participant-preview__gift">
        <ParticipantGiftIcon direction={direction} state={state} />
        <span>
          {t(`box.organizer.giftIndicators.${direction}.${state}`, {
            name: card.username || t('game.people.hidden'),
          })}
        </span>
      </div>
    );
  };
  const date = card.lastActiveAt ? new Date(card.lastActiveAt) : undefined;
  const lastActive =
    date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full', timeStyle: 'short' }).format(
          date,
        )
      : text('never');
  async function revealWish() {
    setLoading(true);
    setError(false);
    try {
      const result = await santaClient.getOtherCard({ key: box.key, id: card.id });
      if (!alive.current) return;
      if (result.ok) setWish(result.data?.wish || '');
      else setError(true);
    } catch {
      if (alive.current) setError(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }
  return (
    <Stack gap={6} wide testId="participant-preview-body">
      <Cluster gap={3}>
        {onEditIdentity ? (
          <button type="button" className="participant-preview__edit-picture" onClick={onEditIdentity}
            aria-label={t('participantIdentity.editIcon')} data-testid="participant-preview-edit-picture">
            <ParticipantFace curiositySource="participant_preview" card={card} index={index} />
          </button>
        ) : <ParticipantFace curiositySource="participant_preview" card={card} index={index} />}
        <Stack gap={2}>
          {onEditIdentity ? (
            <button type="button" className="participant-preview__edit-name" onClick={onEditIdentity}
              aria-label={t('participantIdentity.editName', { name: card.username })}
              data-testid="participant-preview-edit-name">
              <Typography as="span" role="title/sub">{card.username}{card.isYou ? ` (${t('box.organizer.thatsYou')})` : ''}</Typography><PencilIcon />
            </button>
          ) : <Typography as="p" role="title/sub">{card.username}{card.isYou ? ` (${t('box.organizer.thatsYou')})` : ''}</Typography>}
          <span className="participant-preview__status">{t(`box.organizer.status.${status}`)}</span>
        </Stack>
      </Cluster>
      <OrganizerQuestionnaire card={card} />
      {(showLegacyLetter || showLegacyAddress || showLegacyPhone) && (
        <section>
          <Typography as="h3" role="title/sub" className="participant-preview__heading">
            <PencilIcon /> {text('card')}
          </Typography>
          <dl className="participant-preview__fields">
            {showLegacyPhone &&
              row(
                'phone',
                card.phone?.trim() ? (
                  <span className="participant-preview__value">{card.phone}</span>
                ) : card.phoneFilled && !card.phonePreview?.length ? (
                  <span className="participant-preview__value">{text('filled')}</span>
                ) : (
                  filled(card.phonePreview)
                ),
              )}
            {showLegacyAddress && (
              <>
                {row('address', card.postalAddress || filled(card.postalAddressPreview))}
                {row('postalCode', card.postalCode || filled(card.postalCodePreview))}
                {row('recipient', card.postalRecipient || filled(card.postalRecipientPreview))}
              </>
            )}
            {showLegacyLetter &&
              row(
                'wish',
                card.wish?.trim() ? (
                  <WishText>{card.wish}</WishText>
                ) : card.wishPreview?.length ? (
                  <Button
                    variant="ghost"
                    size="small"
                    loading={loading}
                    onClick={() => (wish === undefined ? void revealWish() : setWish(undefined))}
                    testId="participant-preview-wish-toggle"
                  >
                    {text(wish === undefined ? 'show' : 'hide')}
                  </Button>
                ) : (
                  filled(card.wishPreview)
                ),
              )}
          </dl>
          {showLegacyLetter && wish !== undefined && (
            <div className="participant-preview__wish" data-testid="participant-preview-wish">
              <WishText>{wish || text('empty')}</WishText>
            </div>
          )}
          {error && <p role="alert">{text('loadError')}</p>}
        </section>
      )}
      <Divider />
      <section>
        <Typography as="h3" role="title/sub" className="participant-preview__heading">
          <GiftIcon /> {text('gifts')}
        </Typography>
        <div className="participant-preview__gifts">
          {isActiveDraw === false ? (
            <span>{text('noGifteeYet')}</span>
          ) : (
            <>
              {gift('outgoing')}
              {gift('incoming')}
            </>
          )}
        </div>
      </section>
      <section>
        <Typography as="h3" role="title/sub" className="participant-preview__heading">
          <ClockIcon /> {text('activity')}
        </Typography>
        <dl className="participant-preview__fields">{row('lastActive', lastActive)}</dl>
      </section>
      <section>
        <Typography as="h3" role="title/sub" className="participant-preview__heading">
          <UserIcon /> {text('account')}
        </Typography>
        <dl className="participant-preview__fields">
          {row(
            'accountType',
            text(
              card.account
                ? card.account.isGuest
                  ? 'guestAccount'
                  : 'registeredAccount'
                : 'accountUnknown',
            ),
          )}
          {row(
            'accountEmail',
            card.email?.includes('@')
              ? `${card.email.trim().charAt(0)}***@${card.email.trim().split('@')[1]}`
              : text(
                  card.account ? (card.account.hasEmail ? 'added' : 'notAdded') : 'accountUnknown',
                ),
          )}
          {row(
            'socialAccounts',
            <span className="participant-preview__socials">
              {[
                { key: 'vk', name: 'VK', linked: card.account?.hasVk, Icon: VkIcon },
                {
                  key: 'google',
                  name: 'Google',
                  linked: card.account?.hasGoogle,
                  Icon: GoogleIcon,
                },
                {
                  key: 'telegram',
                  name: 'Telegram',
                  linked: card.account?.hasTelegram,
                  Icon: TelegramIcon,
                },
              ].map(({ key, name, linked, Icon }) => {
                const label = `${name}: ${text(linked === undefined ? 'accountUnknown' : linked ? 'connected' : 'notConnected')}`;
                return (
                  <span
                    key={key}
                    role="img"
                    aria-label={label}
                    title={label}
                    tabIndex={0}
                    className={`participant-preview__social participant-preview__social--${key}${linked ? '' : ' participant-preview__social--unlinked'}`}
                  >
                    <Icon />
                  </span>
                );
              })}
            </span>,
          )}
          {row(
            'notifications',
            <span className="participant-preview__notification-list">
              {[
                {
                  key: 'email',
                  labelKey: 'emailNotifications',
                  enabled: card.hasEmailNotifications,
                  Icon: AtIcon,
                },
                {
                  key: 'telegram',
                  labelKey: 'telegramNotifications',
                  enabled: card.hasTelegramNotifications,
                  Icon: TelegramIcon,
                },
              ].map(({ key, labelKey, enabled, Icon }) => {
                const label = `${text(labelKey)}: ${text(enabled ? 'connected' : 'notConnected')}`;
                return (
                  <span
                    key={key}
                    role="img"
                    aria-label={label}
                    title={label}
                    tabIndex={0}
                    className={`participant-preview__notification-status${enabled ? '' : ' participant-preview__notification--disabled'}`}
                  >
                    <span
                      className={`participant-preview__social participant-preview__social--${key}`}
                      aria-hidden="true"
                    >
                      <Icon />
                    </span>
                    <span aria-hidden="true">{text(enabled ? 'connected' : 'notConnected')}</span>
                  </span>
                );
              })}
            </span>,
          )}
        </dl>
      </section>
    </Stack>
  );
}

export function useParticipantPreview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const editIdentity = useParticipantIdentityEditor();
  return (props: PreviewProps, nested = false) =>
    (nested ? pushDialog : showDialog)({
      title: t('box.organizer.preview.title', { name: props.card.username }),
      secondaryAction:
        props.card.isYou && !props.box.isArchived
          ? {
              label: t('game.card.edit'),
              onClick: () => {
                closeDialog();
                navigate(boxCardEditPath(props.box.key));
              },
            }
          : undefined,
      children: <ParticipantPreview key={props.card.id} {...props}
        onEditIdentity={props.card.canOrganizerEditIdentity && !props.card.isYou && !props.box.isArchived && props.onChanged
          ? () => editIdentity({ card: props.card, people: props.people, boxKey: props.box.key, onChanged: props.onChanged! })
          : undefined} />,
      testId: 'participant-preview',
      closeLabel: t('box.close'),
      mobileFullScreen: true,
    });
}
